import { Component, computed, inject, OnInit, signal, ViewChild } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { UiKitModule } from "@app/ui-kit.module";
import { AttachmentSelectorComponent } from "@components/attachment-selector/attachment-selector.component";
import { ConfigurationWizardBackButtonComponent } from "@components/configuration-wizard-back-button/configuration-wizard-back-button.component";
import { ProductConfigStepperBarComponent } from "@components/stepper/product-config-stepper-bar/product-config-stepper-bar.component";
import { FullProductType, LocalizeProductNamePipe, Product } from "@domain/product/product";
import { GasWarningCenterConfiguration } from "@domain/project/configurations/gas-warning-center-configuration";
import { Regard3000Configuration } from "@domain/project/configurations/regard-3000-configuration";
import { Regard7000PlusConfiguration } from "@domain/project/configurations/regard7000PlusConfiguration";
import { FgdsProject as Project } from "@domain/project/fgds-project";
import { WizardComponent } from "@odx/angular/components/wizard";
import { FloorplanMenuType } from "@project/floorplanner/floorplan-menu/fgds/floorplan-menu-type";
import { GasWarningCenterSelectorComponent } from "@project/gas-warning-center-configuration/gas-warning-center-selector/gas-warning-center-selector.component";
import { GasWarningCenterType } from "@project/gas-warning-center-configuration/gas-warning-center-type";
import { GasWarningCenterTypeSelectorComponent } from "@project/gas-warning-center-configuration/gas-warning-center-type-selector/gas-warning-center-type-selector.component";
import { OriginalGasWarningCenterConfiguration } from "@project/gas-warning-center-configuration/original-gas-warning-center-configuration";
import { ProductWithQuantity } from "@project/gas-warning-center-configuration/product-with-quantity";
import { BaseUnitSelectorComponent } from "@project/gas-warning-center-configuration/regard-3000/base-unit-selector/base-unit-selector.component";
import { LidUnitSelectorComponent } from "@project/gas-warning-center-configuration/regard-3000/lid-unit-selector/lid-unit-selector.component";
import { ModuleSelectorComponent } from "@project/gas-warning-center-configuration/regard-3000/module-selector/module-selector.component";
import { Regard3000Service } from "@project/gas-warning-center-configuration/regard-3000/regard-3000.service";
import { AttachmentSelectorRegard7000Component } from "@project/gas-warning-center-configuration/regard-7000-plus/attachment-selector-regard-7000/attachment-selector-regard-7000.component";
import { DashboardSelectorComponent } from "@project/gas-warning-center-configuration/regard-7000-plus/dashboard-selector/dashboard-selector.component";
import { DockingStationSelectorComponent } from "@project/gas-warning-center-configuration/regard-7000-plus/docking-station-selector/docking-station-selector.component";
import { ModuleSelectorRegard7000Component } from "@project/gas-warning-center-configuration/regard-7000-plus/module-selector-regard-7000/module-selector-regard-7000.component";
import { TerminalBlocksSelectorComponent } from "@project/gas-warning-center-configuration/regard-7000-plus/terminal-blocks-selector/terminal-blocks-selector.component";
import { OriginalProductConfigurationFactory } from "@project/original-product-configuration-factory";
import { ProductItem } from "@project/product-item";

export interface Regard3000SelectionState {
  selectedInputModules: ProductWithQuantity[];
  selectedRelayModules: ProductWithQuantity[];
  selectedGatewayModules: ProductWithQuantity[];
  selectedBaseUnits: ProductWithQuantity[];
  selectedBaseUnitProducts: Product[];
  selectedLidUnits: ProductWithQuantity[];
  slotCovers?: ProductWithQuantity;
}
@Component({
  selector: "app-gas-warning-center-configuration",
  templateUrl: "./gas-warning-center-configuration.component.html",
  styleUrls: ["./gas-warning-center-configuration.component.scss"],
  imports: [
    UiKitModule,
    ConfigurationWizardBackButtonComponent,
    GasWarningCenterTypeSelectorComponent,
    GasWarningCenterSelectorComponent,
    AttachmentSelectorComponent,
    ProductConfigStepperBarComponent,
    BaseUnitSelectorComponent,
    ModuleSelectorComponent,
    LidUnitSelectorComponent,
    ModuleSelectorRegard7000Component,
    BaseUnitSelectorComponent,
    DockingStationSelectorComponent,
    DashboardSelectorComponent,
    TerminalBlocksSelectorComponent,
    AttachmentSelectorRegard7000Component,
  ],
  providers: [OriginalProductConfigurationFactory, LocalizeProductNamePipe, Regard3000Service],
  standalone: true,
})
export class GasWarningCenterConfigurationComponent implements OnInit {
  private activeRoute = inject(ActivatedRoute);
  private productConfigurationService = inject(OriginalProductConfigurationFactory);
  private localizeProductNamePipe = inject(LocalizeProductNamePipe);

  protected readonly FloorplanMenuType = FloorplanMenuType;
  protected readonly FullProductType = FullProductType;
  protected readonly selectedDockingStationsRegard7000 = signal<ProductWithQuantity[]>([]);
  protected readonly inputModulesRegard7000 = signal<ProductWithQuantity[]>([]);
  protected readonly relayModulesRegard7000 = signal<ProductWithQuantity[]>([]);
  protected readonly gatewayModulesRegard7000 = signal<ProductWithQuantity[]>([]);
  protected readonly selectedTerminalBlocksRegard7000 = signal<ProductWithQuantity[]>([]);
  protected readonly selectedDashboardsRegard7000 = signal<ProductWithQuantity[]>([]);
  protected readonly selectedRegard7000Products = computed(() => {
    const dockingStations = this.selectedDockingStationsRegard7000();
    if (!dockingStations.length) {
      return [];
    }
    return Array.from(
      new Set(
        [
          ...this.selectedDockingStationsRegard7000(),
          ...this.inputModulesRegard7000(),
          ...this.relayModulesRegard7000(),
          ...this.gatewayModulesRegard7000(),
          ...this.selectedTerminalBlocksRegard7000(),
          ...this.selectedDashboardsRegard7000(),
        ].map((productWithQuantity) => productWithQuantity.product),
      ),
    );
  });
  protected readonly selectedRegard7000Modules = signal<{
    inputModules: ProductWithQuantity[];
    relayModules: ProductWithQuantity[];
    gatewayModules: ProductWithQuantity[];
  }>({
    inputModules: [],
    relayModules: [],
    gatewayModules: [],
  });
  protected readonly necessaryRegard7000SlotCovers = signal<ProductWithQuantity | undefined>(undefined);

  protected project!: Project;
  protected selectedGasWarningCenterTypeForm!: FormGroup;
  protected selectedType?: GasWarningCenterType;
  protected selectedGasWarningCenterForm!: FormGroup;
  protected isBaseUnitSelectionValid: boolean = false;
  protected isLidSelectionValid: boolean = false;
  protected selectedGasWarningCenter?: Product;
  protected selectedAttachments?: ProductWithQuantity[];
  protected steps = 3;
  protected activeStep = 0;
  protected originalConfiguration?: OriginalGasWarningCenterConfiguration;
  protected shouldRestoreOriginalConfiguration = true;

  protected isRegard3000ModularSelected: boolean = false;
  protected regard3000SelectionState: Regard3000SelectionState = {
    selectedInputModules: [],
    selectedRelayModules: [],
    selectedGatewayModules: [],
    selectedBaseUnits: [],
    selectedBaseUnitProducts: [],
    selectedLidUnits: [],
    slotCovers: undefined,
  };
  protected isDockingStationSelectionValid = false;

  @ViewChild("configurationWizard")
  private configurationWizard!: WizardComponent;

  @ViewChild("backButton")
  private backButton!: ConfigurationWizardBackButtonComponent;

  protected stepsDefault = [
    {
      title: $localize`:@@gasWarningCenterConfiguration.wizard.step2:Gaswarnzentrale`,
    },
    {
      title: $localize`:@@gasWarningCenterConfiguration.wizard.step3:Zubehör`,
    },
  ];

  protected stepsRegard3000 = [
    {
      title: $localize`:@@gasWarningCenterConfiguration.wizard.module:Module`,
    },
    {
      title: $localize`:@@gasWarningCenterConfiguration.wizard.baseUnit:Basiseinheit`,
    },
    {
      title: $localize`:@@gasWarningCenterConfiguration.wizard.lid:Deckeleinheit`,
    },
    {
      title: $localize`:@@gasWarningCenterConfiguration.wizard.step3:Zubehör`,
    },
  ];

  protected stepsRegard7000Plus = [
    {
      title: $localize`:@@gasWarningCenterConfiguration.wizard.module:Module`,
    },

    {
      title: $localize`:@@gasWarningCenterConfiguration.wizard.terminal:Klemmblöcke`,
    },

    {
      title: $localize`:@@gasWarningCenterConfiguration.wizard.dockingStation:Dockingstations`,
    },

    {
      title: $localize`:@@gasWarningCenterConfiguration.wizard.dashboards:Dashboards`,
    },

    {
      title: $localize`:@@gasWarningCenterConfiguration.wizard.step3:Zubehör`,
    },
  ];

  protected selectedSteps = this.stepsDefault;

  ngOnInit(): void {
    this.project = this.activeRoute.parent!.snapshot.data["project"];
    if (this.activeRoute.snapshot.queryParams["config_id"]) {
      this.productConfigurationService
        .createOriginalGasWarningCenterConfiguration(this.project, this.activeRoute.snapshot.queryParams["config_id"])
        .subscribe((originalConfiguration) => {
          this.originalConfiguration = originalConfiguration;
        });
    }
  }

  onSubmit() {
    if (this.activeStep === 0) {
      this.selectedGasWarningCenterTypeForm.markAllAsTouched();
      if (!this.selectedGasWarningCenterTypeForm.valid) return;
    }

    if (this.isRegard3000ModularType()) {
      if (this.activeStep === 2 && !this.isBaseUnitSelectionValid) {
        return;
      }
      if (this.activeStep === 3 && !this.isLidSelectionValid) {
        return;
      }
      if (this.activeStep === 4) {
        this.selectedGasWarningCenterForm.markAllAsTouched();
        if (this.originalConfiguration) {
          this.updateGasWarningCenterConfig(this.originalConfiguration.productConfiguration!);
        } else {
          this.createNewGasWarningCenterConfig();
        }
      }
    } else if (this.selectedType?.key === "Regard_7000") {
      if (this.activeStep === 3 && !this.isDockingStationSelectionValid) {
        return;
      }
      if (this.activeStep === 5) {
        if (this.originalConfiguration) {
          this.updateGasWarningCenterConfig(this.originalConfiguration.productConfiguration!);
        } else {
          this.createNewGasWarningCenterConfig();
        }
      }
    } else {
      if (this.activeStep === 1) {
        this.selectedGasWarningCenterForm.markAllAsTouched();
        if (!this.selectedGasWarningCenterForm.valid) return;
      } else if (this.activeStep === 2) {
        if (this.originalConfiguration) {
          this.updateGasWarningCenterConfig(this.originalConfiguration.productConfiguration!);
        } else {
          this.createNewGasWarningCenterConfig();
        }
      }
    }
    this.configurationWizard.setActiveStepValid();
    this.configurationWizard.nextStep();
  }

  protected updateActiveStep($event: number) {
    this.activeStep = $event;
  }

  protected setSelectedType($event: GasWarningCenterType) {
    if (this.selectedType && this.selectedType.key !== $event.key) {
      this.resetSelectionsForSeriesChange();
      this.shouldRestoreOriginalConfiguration = false;
    }

    this.selectedType = $event;
    this.isRegard3000ModularSelected = false;

    if (this.isRegard3000ModularType()) {
      this.isRegard3000ModularSelected = true;
      this.steps = 5;
      this.selectedSteps = this.stepsRegard3000;
    } else if (this.selectedType?.key === "Regard_7000") {
      this.steps = 6;
      this.selectedSteps = this.stepsRegard7000Plus;
    } else {
      this.steps = 3;
      this.selectedSteps = this.stepsDefault;
    }
  }

  protected selectGasWarningCenter(gasWarningCenter?: Product) {
    this.selectedGasWarningCenter = gasWarningCenter;
  }

  protected selectRegard3000BaseUnits(baseUnits: ProductWithQuantity[]) {
    this.regard3000SelectionState.selectedBaseUnits = baseUnits;
    this.regard3000SelectionState.selectedBaseUnitProducts = baseUnits.map((unit) => unit.product);
    this.selectedGasWarningCenter = this.regard3000SelectionState.selectedBaseUnits[0]?.product;
  }

  protected selectRegard3000LidUnits(lidUnits: ProductWithQuantity[]) {
    this.regard3000SelectionState.selectedLidUnits = lidUnits;
  }

  protected selectRegard3000InputModules(modules: ProductWithQuantity[] | []) {
    this.regard3000SelectionState.selectedInputModules = modules;
  }

  protected selectRegard3000RelayModules(modules: ProductWithQuantity[] | []) {
    this.regard3000SelectionState.selectedRelayModules = modules;
  }

  protected selectRegard3000GatewayModules(modules: ProductWithQuantity[] | []) {
    this.regard3000SelectionState.selectedGatewayModules = modules;
  }

  protected updateRegard3000SlotCovers($event: ProductWithQuantity | undefined) {
    if ($event) this.regard3000SelectionState.slotCovers = $event;
  }

  protected selectRegard7000Modules(modules: {
    inputModules: {
      product: Product;
      quantity: number;
    }[];
    relayModules: {
      product: Product;
      quantity: number;
    }[];
    gatewayModules: {
      product: Product;
      quantity: number;
    }[];
  }) {
    this.inputModulesRegard7000.set(modules.inputModules);
    this.relayModulesRegard7000.set(modules.relayModules);
    this.gatewayModulesRegard7000.set(modules.gatewayModules);
    this.selectedRegard7000Modules.set({
      inputModules: modules.inputModules,
      relayModules: modules.relayModules,
      gatewayModules: modules.gatewayModules,
    });
  }

  protected selectRegard7000DockingStations(dockingStations: ProductWithQuantity[]) {
    this.selectedDockingStationsRegard7000.set(dockingStations);
    if (dockingStations.length) {
      this.selectedGasWarningCenter = dockingStations[0].product;
    }
  }

  private resetSelectionsForSeriesChange() {
    this.selectedGasWarningCenter = undefined;
    this.selectedAttachments = undefined;
    this.isBaseUnitSelectionValid = false;
    this.isLidSelectionValid = false;
    this.isDockingStationSelectionValid = false;
    this.regard3000SelectionState = {
      selectedInputModules: [],
      selectedRelayModules: [],
      selectedGatewayModules: [],
      selectedBaseUnits: [],
      selectedBaseUnitProducts: [],
      selectedLidUnits: [],
      slotCovers: undefined,
    };
    this.selectedDockingStationsRegard7000.set([]);
    this.inputModulesRegard7000.set([]);
    this.relayModulesRegard7000.set([]);
    this.gatewayModulesRegard7000.set([]);
    this.selectedTerminalBlocksRegard7000.set([]);
    this.selectedDashboardsRegard7000.set([]);
    this.necessaryRegard7000SlotCovers.set(undefined);
  }

  private createNewGasWarningCenterConfig() {
    let regard3000Configuration;
    let regard7000PlusConfiguration;

    if (this.isRegard3000ModularSelected) {
      this.regard3000SelectionState.slotCovers =
        (this.regard3000SelectionState.slotCovers?.quantity ?? 0) > 0 ? this.regard3000SelectionState.slotCovers : undefined;
      regard3000Configuration = Regard3000Configuration.create(
        this.regard3000SelectionState.selectedInputModules,
        this.regard3000SelectionState.selectedRelayModules,
        this.regard3000SelectionState.selectedGatewayModules,
        this.regard3000SelectionState.selectedBaseUnits,
        this.regard3000SelectionState.selectedLidUnits,
        this.regard3000SelectionState.slotCovers,
      );
    } else if (this.selectedType?.key === "Regard_7000") {
      const dockingStations = this.selectedDockingStationsRegard7000();
      if (!dockingStations.length) {
        return;
      }
      regard7000PlusConfiguration = this.createRegard7000PlusConfiguration(dockingStations);
    }
    const gasWarningCenterConfig = GasWarningCenterConfiguration.create(
      this.localizeProductNamePipe.transform(this.selectedGasWarningCenter!),
      this.project,
      this.selectedGasWarningCenter!.id,
      this.selectedAttachments!.map((selectedAttachment) =>
        ProductItem.create(selectedAttachment.product.id, selectedAttachment.quantity),
      ),
      regard3000Configuration,
      regard7000PlusConfiguration,
    );
    this.project.addGasWarningCenters(gasWarningCenterConfig);
    this.backButton.navigateBack();
  }

  private updateGasWarningCenterConfig(originalConfig: GasWarningCenterConfiguration) {
    const attachments: ProductItem[] = this.selectedAttachments!.map((selectedAttachment) =>
      ProductItem.create(selectedAttachment.product.id, selectedAttachment.quantity),
    );
    this.regard3000SelectionState.slotCovers =
      (this.regard3000SelectionState.slotCovers?.quantity ?? 0) > 0 ? this.regard3000SelectionState.slotCovers : undefined;
    if (this.isRegard3000ModularSelected) {
      const regard3000Configuration = Regard3000Configuration.create(
        this.regard3000SelectionState.selectedInputModules,
        this.regard3000SelectionState.selectedRelayModules,
        this.regard3000SelectionState.selectedGatewayModules,
        this.regard3000SelectionState.selectedBaseUnits,
        this.regard3000SelectionState.selectedLidUnits,
        this.regard3000SelectionState.slotCovers,
      );

      originalConfig.update(this.selectedGasWarningCenter!, attachments, regard3000Configuration);
    } else if (this.selectedType?.key === "Regard_7000") {
      const dockingStations = this.selectedDockingStationsRegard7000();
      if (!dockingStations.length) {
        return;
      }
      const regard7000PlusConfiguration = this.createRegard7000PlusConfiguration(dockingStations);
      originalConfig.update(this.selectedGasWarningCenter!, attachments, undefined, regard7000PlusConfiguration);
    } else {
      originalConfig.update(this.selectedGasWarningCenter!, attachments);
    }
    this.backButton.navigateBack();
  }

  private createRegard7000PlusConfiguration(dockingStations: ProductWithQuantity[]) {
    return new Regard7000PlusConfiguration(
      dockingStations.map((item) => ProductItem.create(item.product.id, item.quantity)),
      this.inputModulesRegard7000().map((item) => ProductItem.create(item.product.id, item.quantity)),
      this.relayModulesRegard7000().map((item) => ProductItem.create(item.product.id, item.quantity)),
      this.gatewayModulesRegard7000().map((item) => ProductItem.create(item.product.id, item.quantity)),
      this.selectedTerminalBlocksRegard7000().map((item) => ProductItem.create(item.product.id, item.quantity)),
      this.selectedDashboardsRegard7000().map((item) => ProductItem.create(item.product.id, item.quantity)),
    );
  }

  private isRegard3000ModularType(): boolean {
    return this.selectedType?.key === "Regard_3000" || this.selectedType?.key === "Regard_3000_plus";
  }
}
